'use client';

import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import classnames from 'classnames';
import { es } from 'date-fns/locale/es';
import { default as ReactDatePicker, registerLocale } from 'react-datepicker';

import { dateDiffInDays, getUTCTimezoneOffset } from '../../utils/date';
import { Button } from '../button';
import { Popover } from '../popover';
import { PopoverContent, PopoverTrigger } from '../popover/Popover';
import type { DatePickerProps } from './DatePicker.types';
import { DATE_PICKER_LABELS } from './labels';
import { TimePicker } from './layout/TimePicker';

// sort-imports-ignore
import './react-datepicker-vendor.scss';
import styles from './DatePicker.module.scss';

registerLocale('es', es);

const YESTERDAY_DAYS = 1;
const LAST_SEVEN_DAYS = 7;
const MINUTE_MS = 60000;

function DatePicker({
  style,
  className,
  onSubmit,
  onChange,
  startDate = new Date(),
  endDate = new Date(),
  isSubmitting = false,
  labels,
  locale = 'en',
  size = 'lg',
  shortcuts = [],
  testId = 'ui-date-picker',
}: DatePickerProps) {
  const [start, setStart] = useState<Date | undefined>(startDate);
  const [end, setEnd] = useState<Date | undefined>(endDate);
  const text = { ...DATE_PICKER_LABELS[locale], ...labels };

  const [timezoneOffset, setTimezoneOffset] = useState<string>('UTC');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimezoneOffset(getUTCTimezoneOffset(new Date()));
    }, MINUTE_MS);

    setTimezoneOffset(getUTCTimezoneOffset(new Date()));

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (onChange && start && end) {
      onChange(start, end);
    }
  }, [start, end]);

  useEffect(() => {
    setStart(startDate);
  }, [startDate]);

  useEffect(() => {
    setEnd(endDate);
  }, [endDate]);

  const reset = () => {
    flushSync(() => {
      setStart(undefined);
      setEnd(undefined);
    });
  };

  const getTodayDates = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onToday = () => {
    reset();

    const { startDate, endDate } = getTodayDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getYesterdayDates = () => {
    const startDate = new Date();
    startDate.setDate(new Date().getDate() - YESTERDAY_DAYS);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setDate(new Date().getDate() - YESTERDAY_DAYS);
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onYesterday = () => {
    reset();

    const { startDate, endDate } = getYesterdayDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getLastSevenDaysDates = () => {
    const startDate = new Date();
    startDate.setDate(new Date().getDate() - LAST_SEVEN_DAYS);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onLastSevenDays = () => {
    reset();

    const { startDate, endDate } = getLastSevenDaysDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getMonthToDateDates = () => {
    const startDate = new Date();
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onMonthToDate = () => {
    reset();

    const { startDate, endDate } = getMonthToDateDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getMonthFromDateDates = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onMonthFromDate = () => {
    reset();

    const { startDate, endDate } = getMonthFromDateDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getNextMonthFromDateDates = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setMonth(startDate.getMonth() + 1);
    endDate.setDate(startDate.getDate());
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onNextMonthFromDate = () => {
    reset();

    const { startDate, endDate } = getNextMonthFromDateDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getYearToDateDates = () => {
    const startDate = new Date();
    startDate.setMonth(0);
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onYearToDate = () => {
    reset();

    const { startDate, endDate } = getYearToDateDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getYearFromDateDates = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setFullYear(startDate.getFullYear() + 1);
    endDate.setMonth(0);
    endDate.setDate(0);
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onYearFromDate = () => {
    reset();

    const { startDate, endDate } = getYearFromDateDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const getNextYearFromDateDates = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);
    endDate.setDate(startDate.getDate());
    endDate.setHours(23, 59, 59, 0);

    return { startDate, endDate };
  };

  const onNextYearFromDate = () => {
    reset();

    const { startDate, endDate } = getNextYearFromDateDates();

    setStart(startDate);
    setEnd(endDate);
  };

  const formatDate = (date: Date, locale: string) => {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Popover placement="bottom" open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild={true} onClick={() => setOpen((v) => !v)}>
        <Button
          style={style}
          className={className}
          variant="outline-primary"
          data-testid={`${testId}_selector`}
        >
          {formatDate(new Date(start ?? new Date()), locale)} -{' '}
          {formatDate(new Date(end ?? new Date()), locale)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={styles['popover-content']}>
        <div className={styles['container']}>
          <div
            className={classnames(
              styles['content'],
              size && styles[`content--${size}`],
            )}
          >
            {shortcuts.length > 0 && (
              <div
                className={classnames(
                  styles['shortcuts'],
                  size && styles[`shortcuts--${size}`],
                )}
                data-testid={`${testId}_shortcuts`}
              >
                {shortcuts.includes('today') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getTodayDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getTodayDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onToday}
                    data-testid={`${testId}_today`}
                  >
                    {text.today}
                  </Button>
                )}
                {shortcuts.includes('yesterday') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getYesterdayDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getYesterdayDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onYesterday}
                    data-testid={`${testId}_yesterday`}
                  >
                    {text.yesterday}
                  </Button>
                )}
                {shortcuts.includes('lastSevenDays') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getLastSevenDaysDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getLastSevenDaysDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onLastSevenDays}
                    data-testid={`${testId}_lastSevenDays`}
                  >
                    {text.lastSevenDays}
                  </Button>
                )}
                {shortcuts.includes('monthToDate') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getMonthToDateDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getMonthToDateDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onMonthToDate}
                    data-testid={`${testId}_monthToDate`}
                  >
                    {text.monthToDate}
                  </Button>
                )}
                {shortcuts.includes('monthFromDate') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getMonthFromDateDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getMonthFromDateDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onMonthFromDate}
                    data-testid={`${testId}_monthFromDate`}
                  >
                    {text.monthFromDate}
                  </Button>
                )}
                {shortcuts.includes('nextMonthFromDate') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getNextMonthFromDateDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getNextMonthFromDateDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onNextMonthFromDate}
                    data-testid={`${testId}_nextMonthFromDate`}
                  >
                    {text.nextMonthFromDate}
                  </Button>
                )}
                {shortcuts.includes('yearToDate') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getYearToDateDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getYearToDateDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onYearToDate}
                    data-testid={`${testId}_yearToDate`}
                  >
                    {text.yearToDate}
                  </Button>
                )}
                {shortcuts.includes('yearFromDate') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getYearFromDateDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getYearFromDateDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onYearFromDate}
                    data-testid={`${testId}_yearFromDate`}
                  >
                    {text.yearFromDate}
                  </Button>
                )}
                {shortcuts.includes('nextYearFromDate') && (
                  <Button
                    className={styles['shortcuts__button']}
                    variant={
                      dateDiffInDays(
                        getNextYearFromDateDates().startDate,
                        new Date(start ?? new Date()),
                      ) === 0 &&
                      dateDiffInDays(
                        getNextYearFromDateDates().endDate,
                        new Date(end ?? new Date()),
                      ) === 0
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={onNextYearFromDate}
                    data-testid={`${testId}_nextYearFromDate`}
                  >
                    {text.nextYearFromDate}
                  </Button>
                )}
              </div>
            )}
            {size === 'lg' && (
              <div
                className={classnames(
                  styles['disposition'],
                  size && styles[`disposition--${size}`],
                )}
              >
                <div
                  className={classnames(styles['wrapper'])}
                  data-testid={`${testId}_fromDatePicker`}
                >
                  <ReactDatePicker
                    selected={start}
                    onChange={(date) => {
                      if (date) {
                        setStart(
                          new Date(
                            date.getFullYear(),
                            date.getMonth(),
                            date.getDate(),
                            0,
                            0,
                            0,
                          ),
                        );

                        if (end && end < date) {
                          setEnd(
                            new Date(
                              date.getFullYear(),
                              date.getMonth(),
                              date.getDate(),
                              23,
                              59,
                              59,
                            ),
                          );
                        }
                      }
                    }}
                    selectsStart
                    startDate={start}
                    endDate={end}
                    inline={size === 'lg'}
                    disabledKeyboardNavigation
                    showPopperArrow={false}
                    locale={locale}
                  />
                  <TimePicker
                    testId={`${testId}_fromTimePicker`}
                    date={start ?? new Date()}
                    minDate={
                      start
                        ? new Date(
                            start.getFullYear(),
                            start.getMonth(),
                            start.getDate(),
                            0,
                            0,
                            0,
                          )
                        : new Date()
                    }
                    maxDate={
                      end
                        ? new Date(
                            end.getFullYear(),
                            end.getMonth(),
                            end.getDate(),
                            end.getHours(),
                            end.getMinutes(),
                            end.getSeconds(),
                          )
                        : new Date()
                    }
                    onChange={(date: Date) => {
                      setStart(date);
                    }}
                  />
                </div>
                <div
                  className={classnames(styles['wrapper'])}
                  data-testid={`${testId}_toDatePicker`}
                >
                  <ReactDatePicker
                    selected={end}
                    onChange={(date) => {
                      if (date) {
                        setEnd(
                          new Date(
                            date.getFullYear(),
                            date.getMonth(),
                            date.getDate(),
                            23,
                            59,
                            59,
                          ),
                        );
                      }
                    }}
                    selectsEnd
                    startDate={start}
                    endDate={end}
                    minDate={start}
                    inline={size === 'lg'}
                    disabledKeyboardNavigation
                    showPopperArrow={false}
                    locale={locale}
                  />
                  <TimePicker
                    testId={`${testId}_toTimePicker`}
                    date={end ?? new Date()}
                    minDate={
                      start
                        ? new Date(
                            start.getFullYear(),
                            start.getMonth(),
                            start.getDate(),
                            start.getHours(),
                            start.getMinutes(),
                            start.getSeconds(),
                          )
                        : new Date()
                    }
                    maxDate={
                      end
                        ? new Date(
                            end.getFullYear(),
                            end.getMonth(),
                            end.getDate(),
                            23,
                            59,
                            59,
                          )
                        : new Date()
                    }
                    onChange={(date: Date) => {
                      setEnd(date);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {onSubmit && (
            <div
              className={classnames(
                styles['footer'],
                size && styles[`footer--${size}`],
              )}
            >
              <span className={styles['footer__utc']}>{timezoneOffset}</span>
              <div
                className={classnames(
                  styles['footer__button'],
                  size && styles[`footer__button--${size}`],
                )}
              >
                <Button
                  variant="primary"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  onClick={() => {
                    if (onSubmit) {
                      onSubmit(start ?? new Date(), end ?? new Date());
                    }
                    setOpen(false);
                  }}
                  data-testid={`${testId}_submit`}
                >
                  {text.submit}
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
